import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Check, Loader2, RefreshCw, School, X } from 'lucide-react';
import { apiGet, apiPost } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

/**
 * Schools students have added themselves.
 *
 * A child whose school isn't on the list can use their own spelling straight
 * away — being stuck at signup is worse than a typo — but it stays off the
 * public dropdown until someone here confirms the name. Correct the spelling in
 * the box, then Add: everyone who typed that name is moved onto the approved
 * spelling, so one school never becomes five.
 */
export function SchoolSuggestions() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState('pending');
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(() => {
    apiGet(`/admin/schools/suggestions?status=${status}`)
      .then(d => { setRows(d.suggestions || []); setCounts(d.counts || {}); setError(''); })
      .catch(e => setError(e.message));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const decide = async (row, action) => {
    setBusy(row.suggestion_id);
    try {
      const e = edits[row.suggestion_id] || {};
      await apiPost(`/admin/schools/suggestions/${row.suggestion_id}/${action}`, {
        name: e.name ?? row.name, city: e.city ?? row.city, state: e.state ?? row.state,
      });
      toast.success(action === 'approve' ? 'Added to the school list' : 'Suggestion rejected');
      load();
    } catch (ex) { toast.error(ex?.message || 'That did not go through'); }
    setBusy('');
  };

  const set = (id, patch) => setEdits(e => ({ ...e, [id]: { ...(e[id] || {}), ...patch } }));

  return (
    <section className="space-y-4 border-b pb-6 min-w-0" data-testid="school-suggestions">
      <div className="flex flex-wrap gap-3 items-center">
        <h2 className="font-heading text-lg flex-1 flex items-center gap-2">
          <School className="h-4 w-4 text-slate-400" /> New schools from students
        </h2>
        <Button variant="outline" size="sm" onClick={load} data-testid="school-suggestions-refresh">
          <RefreshCw className="h-4 w-4 mr-2" />Refresh
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">
        Check the spelling against the school's own documents, fix it if needed, then Add — the student
        keeps using their version in the meantime, and moves onto yours once you approve.
      </p>

      <div className="flex flex-wrap gap-2">
        {['pending', 'approved', 'rejected', 'all'].map(s => (
          <Button key={s} size="sm" variant={s === status ? 'default' : 'outline'} onClick={() => setStatus(s)}
            data-testid={`school-suggestions-${s}`}>
            {s[0].toUpperCase() + s.slice(1)}{counts[s] !== undefined ? ` (${counts[s]})` : ''}
          </Button>
        ))}
      </div>

      {error && <p role="alert" className="text-sm text-destructive" data-testid="school-suggestions-error">{error}</p>}
      {!rows.length && <p className="text-sm text-muted-foreground py-3" data-testid="school-suggestions-empty">
        Nothing here — no student has added a school in this view.
      </p>}

      <div className="overflow-x-auto rounded-xl border border-slate-200">
        {!!rows.length && (
          <table className="w-full text-sm" data-testid="school-suggestions-table">
            <thead className="bg-slate-50/70 text-slate-500 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">School name</th>
                <th className="text-left px-3 py-2">City / State</th>
                <th className="text-left px-3 py-2">Added by</th>
                <th className="text-center px-3 py-2">Status</th>
                <th className="text-center px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.suggestion_id} className="border-t border-slate-100 align-top hover:bg-slate-50/60"
                    data-testid={`school-suggestion-${r.suggestion_id}`}>
                  <td className="px-3 py-2.5 min-w-[14rem]">
                    <Input value={edits[r.suggestion_id]?.name ?? r.name} disabled={r.status !== 'pending'}
                      onChange={e => set(r.suggestion_id, { name: e.target.value })}
                      data-testid={`school-suggestion-name-${r.suggestion_id}`} className="h-8 text-[13px]" />
                    {(r.also_suggested_by || []).length > 1 && (
                      <p className="text-[11px] text-slate-500 mt-1">{r.also_suggested_by.length} students typed this</p>
                    )}
                  </td>
                  <td className="px-3 py-2.5 min-w-[12rem]">
                    <Input value={edits[r.suggestion_id]?.city ?? r.city ?? ''} disabled={r.status !== 'pending'}
                      onChange={e => set(r.suggestion_id, { city: e.target.value })} placeholder="City"
                      data-testid={`school-suggestion-city-${r.suggestion_id}`} className="h-8 text-[13px]" />
                    <p className="text-[11px] text-slate-400 mt-1">{r.state || '—'}</p>
                  </td>
                  <td className="px-3 py-2.5 text-slate-600">
                    <p className="break-words">{r.suggested_by_name || '—'}</p>
                    <p className="text-[11px] text-slate-400">{(r.created_at || '').slice(0, 10)}</p>
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <Badge className={`rounded-full text-[10px] uppercase tracking-wider ${
                      r.status === 'approved' ? 'bg-emerald-100 text-emerald-700'
                        : r.status === 'rejected' ? 'bg-slate-200 text-slate-600'
                          : 'bg-amber-100 text-amber-700'}`}>{r.status}</Badge>
                  </td>
                  <td className="px-3 py-2.5">
                    {r.status === 'pending' ? (
                      <div className="flex gap-2 justify-center">
                        <Button size="sm" disabled={busy === r.suggestion_id} onClick={() => decide(r, 'approve')}
                          data-testid={`school-suggestion-approve-${r.suggestion_id}`}>
                          {busy === r.suggestion_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}Add
                        </Button>
                        <Button size="sm" variant="outline" disabled={busy === r.suggestion_id}
                          onClick={() => decide(r, 'reject')} data-testid={`school-suggestion-reject-${r.suggestion_id}`}>
                          <X className="h-4 w-4 mr-1" />Reject
                        </Button>
                      </div>
                    ) : <p className="text-center text-[11px] text-slate-400">{r.approved_name || r.reason || '—'}</p>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

export default SchoolSuggestions;
